import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {IAccountForm, IAccountFormPayload, IMedicalSpecialty} from '@app/accounts/models/accounts.model';
import {IRole} from '@app/accounts/models/role.model';

import {
  loadRoles,
  resetAddAccountStatus,
  saveAccount,
  updateAddAccountStatus,
} from '@app/accounts/states/accounts.actions';
import {selectAddAccountStatus, selectRoles} from '@app/accounts/states/accounts.selector';
import {Email_Regex, MinChars_Regex} from '@app/shared/constants';
import {EActionStatus} from '@app/shared/enums/action-status.enum';
import {ERole} from '@app/shared/enums/role.enum';
import {ValidationService} from '@app/shared/services/validation.service';
import {selectCustomerOrganizationId} from '@app/shared/state/header/header.selector';
import {loadMedicalSpecialties} from '@app/signUp/state/sign-up.actions';
import {selectMedicalSpecialties} from '@app/signUp/state/sign-up.selector';
import {Store} from '@ngrx/store';
import {Observable, Subject, map, takeUntil, tap} from 'rxjs';
import { Bind } from 'primeng/bind';
import { FloatLabel } from 'primeng/floatlabel';
import { InputText } from 'primeng/inputtext';
import { Message } from 'primeng/message';
import { Select } from 'primeng/select';
import { Checkbox } from 'primeng/checkbox';
import { Button } from 'primeng/button';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-add-account',
    templateUrl: './add-account.component.html',
    styleUrl: './add-account.component.scss',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        Bind,
        FloatLabel,
        InputText,
        Message,
        Select,
        Checkbox,
        Button,
        AsyncPipe,
        TranslatePipe,
    ],
})
export class AddAccountComponent implements OnInit, OnDestroy {
  private readonly formBuilder = inject(FormBuilder);
  private readonly validationService = inject(ValidationService);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly route = inject(ActivatedRoute);

  addAccountForm: FormGroup<IAccountForm>;
  medicalSpecialties$: Observable<IMedicalSpecialty[]>;
  roles$: Observable<IRole[]> | undefined;
  saveInProgress = false;
  isQualifiedProfessionalVisible = true;
  private readonly unsubscribe$ = new Subject<void>();

  userRoles: IRole[] = [];
  organizationId: number = 0;

  constructor() {
    this.medicalSpecialties$ = this.store.select(selectMedicalSpecialties).pipe(
      takeUntil(this.unsubscribe$),
      map(medicalSpecialties => medicalSpecialties ?? [])
    );

    this.addAccountForm = this.formBuilder.group({
      firstName: [
        '',
        [
          Validators.required,
          this.validationService.charactersDiacriticsWithMaxLimt,
          Validators.pattern(MinChars_Regex),
        ],
      ],
      lastName: [
        '',
        [
          Validators.required,
          this.validationService.charactersDiacriticsWithMaxLimt,
          Validators.pattern(MinChars_Regex),
        ],
      ],
      email: ['', [Validators.required, Validators.pattern(Email_Regex)]],
      medicalSpecialty: new FormControl<number | null>(null, Validators.required),
      doctorId: ['', [this.validationService.specialCharacterWithDigits]],
      role: new FormControl<number | null>(null, Validators.required),
      isQualifiedProfessional: [true, [Validators.requiredTrue]],
    });

    this.patchDoctorRole();
    this.store
      .select(selectCustomerOrganizationId)
      .pipe(
        tap(organizationId => {
          this.organizationId = organizationId ?? 0;
        })
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.loadMedicalSpecialties();
    this.store.dispatch(loadRoles());
    this.checkQualifiedProfessional();
    this.store.dispatch(resetAddAccountStatus());
    this.redirectToAccountsSubscription();
  }

  onAddAccountSubmit(): void {
    if (this.addAccountForm.valid) {
      this.saveInProgress = true;
      this.store.dispatch(updateAddAccountStatus({status: EActionStatus.Pending}));
      this.store.dispatch(saveAccount({payload: this.getAddAccountPayload()}));
    }
  }

  onAddAccountCancel(): void {
    this.router.navigate([`/accounts`]);
  }

  private loadMedicalSpecialties(): void {
    this.store.dispatch(loadMedicalSpecialties());
  }

  private patchDoctorRole() {
    this.roles$ = this.store.select(selectRoles).pipe(
      tap(roles => {
        this.userRoles = roles;
        const role = roles.find(role => role.name === ERole.Doctor);
        this.addAccountForm.patchValue({
          role: role?.id,
        });
      })
    );
  }

  private checkQualifiedProfessional() {
    this.addAccountForm.get('role')?.valueChanges.subscribe(value => {
      const role = this.userRoles.find(role => role.name === ERole.Doctor);
      if (value === role?.id) {
        this.addAccountForm.get('isQualifiedProfessional')?.setValidators([Validators.requiredTrue]);
        this.isQualifiedProfessionalVisible = true;
      } else {
        this.addAccountForm.get('isQualifiedProfessional')?.clearValidators();
        this.isQualifiedProfessionalVisible = false;
      }
      this.addAccountForm.get('isQualifiedProfessional')?.updateValueAndValidity();
    });
  }

  private getAddAccountPayload(): IAccountFormPayload {
    const payload = {} as IAccountFormPayload;

    if (this.addAccountForm.value.firstName) {
      payload.firstName = this.addAccountForm.value.firstName;
    }

    if (this.addAccountForm.value.lastName) {
      payload.lastName = this.addAccountForm.value.lastName;
    }

    if (this.addAccountForm.value.email) {
      payload.email = this.addAccountForm.value.email;
    }

    if (this.addAccountForm.value.doctorId) {
      payload.doctorId = this.addAccountForm.value.doctorId;
    }

    if (this.addAccountForm.value.medicalSpecialty) {
      payload.medicalSpecialtyId = this.addAccountForm.value.medicalSpecialty;
    }

    if (this.addAccountForm.value.role) {
      payload.roleId = this.addAccountForm.value.role;
    }

    if (this.organizationId) {
      payload.organizationId = this.organizationId;
    }

    return payload;
  }

  private redirectToAccountsSubscription(): void {
    this.store
      .select(selectAddAccountStatus)
      .pipe(
        takeUntil(this.unsubscribe$),
        map((addAccountStatus: EActionStatus) => {
          if (addAccountStatus === EActionStatus.Success || addAccountStatus === EActionStatus.Failure) {
            this.saveInProgress = false;
          }
          if (addAccountStatus === EActionStatus.Success) {
            this.router.navigate([`/accounts`]);
          }
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
